import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiPlus,
  FiX,
  FiRefreshCw,
  FiMessageSquare,
  FiPaperclip,
  FiCreditCard,
  FiAlertCircle,
} from 'react-icons/fi';
import {
  mockCreditsAPI,
  MockCreditPurchaseRequest,
  MockCreditSummary,
  MockCreditsInfo,
} from '../services/api';
import { getMockCreditAttachmentUrl } from '../config/api';
import { notifyMockCreditTicketsUpdated } from '../utils/adminMockCreditEvents';
import './MockCreditPanel.css';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function statusLabel(req: MockCreditPurchaseRequest): string {
  if (req.status === 'open' && req.awaiting_user) return 'Action required';
  if (req.status === 'open') return 'Under review';
  return req.status;
}

function statusClass(status: string): string {
  return `mock-credits-status mock-credits-status-${status}`;
}

interface MockCreditPanelProps {
  creditsInfo?: MockCreditsInfo | null;
  onCreditsChange?: () => void;
}

const MockCreditPanel: React.FC<MockCreditPanelProps> = ({ creditsInfo, onCreditsChange }) => {
  const [summary, setSummary] = useState<MockCreditSummary | null>(null);
  const [requests, setRequests] = useState<MockCreditPurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<MockCreditPurchaseRequest | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createForm, setCreateForm] = useState({
    requested_credits: 1,
    contact_phone: '',
    contact_email: '',
    initial_message: '',
    file: null as File | null,
  });

  const [replyBody, setReplyBody] = useState('');
  const [replyFile, setReplyFile] = useState<File | null>(null);

  const balance = creditsInfo?.unlimited
    ? 'Unlimited'
    : String(creditsInfo?.balance ?? summary?.balance ?? 0);
  const canTakeMock = creditsInfo?.can_take_mock ?? summary?.can_take_mock ?? false;
  const unlimited = creditsInfo?.unlimited ?? summary?.unlimited ?? false;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [sum, reqs] = await Promise.all([
        mockCreditsAPI.getSummary(),
        mockCreditsAPI.listRequests(),
      ]);
      setSummary(sum);
      setRequests(reqs);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load mock credits';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openTicket = async (id: number) => {
    try {
      setError(null);
      const detail = await mockCreditsAPI.getRequest(id);
      setSelectedRequest(detail);
      setReplyBody('');
      setReplyFile(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    }
  };

  const handleCreate = async () => {
    if (createForm.requested_credits < 1) {
      setError('Please request at least 1 mock credit');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await mockCreditsAPI.createRequest({
        requested_credits: createForm.requested_credits,
        contact_phone: createForm.contact_phone || undefined,
        contact_email: createForm.contact_email || undefined,
        initial_message: createForm.initial_message || undefined,
        file: createForm.file,
      });
      setShowCreateModal(false);
      setCreateForm({
        requested_credits: 1,
        contact_phone: '',
        contact_email: '',
        initial_message: '',
        file: null,
      });
      await loadData();
      onCreditsChange?.();
      notifyMockCreditTicketsUpdated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!selectedRequest) return;
    if (!replyBody.trim() && !replyFile) {
      setError('Enter a message or attach a file');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const updated = await mockCreditsAPI.addMessage(selectedRequest.id, replyBody, replyFile);
      setSelectedRequest(updated);
      setReplyBody('');
      setReplyFile(null);
      await loadData();
      notifyMockCreditTicketsUpdated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedRequest || selectedRequest.status !== 'open') return;
    if (!window.confirm('Cancel this purchase request?')) return;
    try {
      setSubmitting(true);
      await mockCreditsAPI.cancelRequest(selectedRequest.id);
      setSelectedRequest(null);
      await loadData();
      notifyMockCreditTicketsUpdated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to cancel');
    } finally {
      setSubmitting(false);
    }
  };

  const renderAttachment = (reqId: number, att: { id: number; file_name: string; mime_type: string }) => {
    const url = getMockCreditAttachmentUrl(reqId, att.id);
    const isImage = att.mime_type.startsWith('image/');
    return (
      <div key={att.id} className="mock-credits-attachments">
        {url && isImage ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            <img src={url} alt={att.file_name} className="mock-credits-attachment-preview" />
          </a>
        ) : null}
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className="mock-credits-attachment-link">
            {React.createElement(FiPaperclip as React.ComponentType<{ size?: number }>, { size: 14 })}
            {att.file_name}
          </a>
        ) : null}
      </div>
    );
  };

  return (
    <section className="mock-credits-panel">
      <div className="mock-credits-header">
        <div className="mock-credits-balance-block">
          <h2>Mock credits</h2>
          <div className="mock-credits-balance">{loading ? '…' : balance}</div>
          <p className="mock-credits-balance-sub">
            {unlimited
              ? 'Staff account — unlimited mock tests'
              : 'Each mock test uses 1 credit. A subscription is not required.'}
          </p>
        </div>
        <div className="mock-credits-actions">
          <button type="button" className="mock-credits-btn mock-credits-btn-primary" onClick={() => setShowCreateModal(true)}>
            {React.createElement(FiPlus as React.ComponentType<{ size?: number }>, { size: 16 })}
            Buy credits
          </button>
          <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={loadData} disabled={loading}>
            {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number; className?: string }>, {
              size: 16,
              className: loading ? 'spinner' : undefined,
            })}
            Refresh
          </button>
        </div>
      </div>

      {!unlimited && !canTakeMock ? (
        <div className="mock-credits-alert mock-credits-alert-warn">
          {React.createElement(FiAlertCircle as React.ComponentType<{ size?: number }>, { size: 16 })}
          {' '}
          You have no mock credits. Submit your payment receipt below — credits will be added to your account once approved.
        </div>
      ) : null}

      {summary && summary.open_requests_count > 0 ? (
        <div className="mock-credits-alert mock-credits-alert-info">
          {summary.open_requests_count} open request{summary.open_requests_count > 1 ? 's' : ''} — we will notify you here when there is an update.
        </div>
      ) : null}

      {error ? (
        <div className="mock-credits-alert mock-credits-alert-warn">{error}</div>
      ) : null}

      <h3 className="mock-credits-tickets-title">Purchase requests</h3>
      {loading ? (
        <p className="mock-credits-tickets-empty">Loading…</p>
      ) : requests.length === 0 ? (
        <p className="mock-credits-tickets-empty">No requests yet. Click &quot;Buy credits&quot; to purchase mock credits.</p>
      ) : (
        <div className="mock-credits-ticket-list">
          {requests.map((req) => (
            <div
              key={req.id}
              className="mock-credits-ticket-row"
              onClick={() => openTicket(req.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && openTicket(req.id)}
            >
              <div>
                <span className="mock-credits-ticket-id">Ticket #{req.id}</span>
                <span className="mock-credits-ticket-meta">
                  {' '}
                  — {req.requested_credits} credit{req.requested_credits > 1 ? 's' : ''} requested · {formatDate(req.created_at)}
                </span>
              </div>
              <span className={statusClass(req.status === 'open' && req.awaiting_user ? 'open' : req.status)}>
                {statusLabel(req)}
              </span>
            </div>
          ))}
        </div>
      )}

      {summary && summary.ledger.length > 0 ? (
        <div className="mock-credits-ledger">
          <h4>Recent activity</h4>
          {summary.ledger.slice(0, 5).map((e) => (
            <div key={e.id} className="mock-credits-ledger-row">
              <span>{formatDate(e.created_at)} — {e.note || e.entry_type.replace(/_/g, ' ')}</span>
              <span className={e.amount >= 0 ? 'mock-credits-ledger-amount-pos' : 'mock-credits-ledger-amount-neg'}>
                {e.amount >= 0 ? '+' : ''}{e.amount}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      <AnimatePresence>
        {showCreateModal ? (
          <motion.div
            className="mock-credits-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !submitting && setShowCreateModal(false)}
          >
            <motion.div
              className="mock-credits-modal"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mock-credits-modal-header">
                <h3>
                  {React.createElement(FiCreditCard as React.ComponentType<{ size?: number }>, { size: 20 })}
                  {' '}
                  Request mock credits
                </h3>
                <button type="button" className="mock-credits-modal-close" onClick={() => setShowCreateModal(false)}>
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 22 })}
                </button>
              </div>
              <div className="mock-credits-modal-body">
                <p style={{ color: '#aaa', fontSize: '0.9rem', marginTop: 0 }}>
                  Open a ticket for the number of mock tests you want to purchase. Include contact details and optionally attach a payment receipt. An admin will review and add credits to your account.
                </p>
                <div className="mock-credits-form-group">
                  <label htmlFor="req-credits">Number of mock credits</label>
                  <select
                    id="req-credits"
                    value={createForm.requested_credits}
                    onChange={(e) => setCreateForm((f) => ({ ...f, requested_credits: Number(e.target.value) }))}
                  >
                    {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                  </select>
                </div>
                <div className="mock-credits-form-row">
                  <div className="mock-credits-form-group">
                    <label htmlFor="req-phone">Contact phone</label>
                    <input
                      id="req-phone"
                      type="tel"
                      value={createForm.contact_phone}
                      onChange={(e) => setCreateForm((f) => ({ ...f, contact_phone: e.target.value }))}
                      placeholder="+61 …"
                    />
                  </div>
                  <div className="mock-credits-form-group">
                    <label htmlFor="req-email">Contact email</label>
                    <input
                      id="req-email"
                      type="email"
                      value={createForm.contact_email}
                      onChange={(e) => setCreateForm((f) => ({ ...f, contact_email: e.target.value }))}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div className="mock-credits-form-group">
                  <label htmlFor="req-msg">Message (payment method, reference, etc.)</label>
                  <textarea
                    id="req-msg"
                    value={createForm.initial_message}
                    onChange={(e) => setCreateForm((f) => ({ ...f, initial_message: e.target.value }))}
                    placeholder="Describe how you paid or will pay, transaction reference…"
                  />
                </div>
                <div className="mock-credits-form-group">
                  <label htmlFor="req-file">Payment receipt (optional)</label>
                  <input
                    id="req-file"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setCreateForm((f) => ({ ...f, file: e.target.files?.[0] ?? null }))}
                  />
                </div>
              </div>
              <div className="mock-credits-modal-footer">
                <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="button" className="mock-credits-btn mock-credits-btn-primary" onClick={handleCreate} disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit request'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {selectedRequest ? (
          <motion.div
            className="mock-credits-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !submitting && setSelectedRequest(null)}
          >
            <motion.div
              className="mock-credits-modal mock-credits-modal-wide"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mock-credits-modal-header">
                <h3>
                  {React.createElement(FiMessageSquare as React.ComponentType<{ size?: number }>, { size: 20 })}
                  {' '}
                  Ticket #{selectedRequest.id}
                </h3>
                <button type="button" className="mock-credits-modal-close" onClick={() => setSelectedRequest(null)}>
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 22 })}
                </button>
              </div>
              <div className="mock-credits-modal-body">
                <p style={{ color: '#aaa', fontSize: '0.85rem', marginTop: 0 }}>
                  {selectedRequest.requested_credits} credits requested
                  {selectedRequest.granted_credits != null ? ` · ${selectedRequest.granted_credits} granted` : ''}
                  {' · '}
                  <span className={statusClass(selectedRequest.status)}>{statusLabel(selectedRequest)}</span>
                </p>
                {selectedRequest.admin_resolution_note && selectedRequest.status !== 'open' ? (
                  <div className="mock-credits-alert mock-credits-alert-info" style={{ marginBottom: '1rem' }}>
                    {selectedRequest.admin_resolution_note}
                  </div>
                ) : null}
                <div className="mock-credits-thread">
                  {(selectedRequest.messages || []).map((msg) => (
                    <div
                      key={msg.id}
                      className={`mock-credits-msg mock-credits-msg-${msg.author_role}`}
                    >
                      <div className="mock-credits-msg-role">{msg.author_role === 'admin' ? 'Admin' : 'You'}</div>
                      <div className="mock-credits-msg-body">{msg.body}</div>
                      <div className="mock-credits-msg-time">{formatDate(msg.created_at)}</div>
                      {msg.attachments?.map((att) => renderAttachment(selectedRequest.id, att))}
                    </div>
                  ))}
                </div>
                {(selectedRequest.attachments || [])
                  .filter((a) => !(selectedRequest.messages || []).some((m) => m.attachments?.some((ma) => ma.id === a.id)))
                  .map((att) => renderAttachment(selectedRequest.id, att))}

                {selectedRequest.status === 'open' ? (
                  <div className="mock-credits-reply-box">
                    <div className="mock-credits-form-group">
                      <label htmlFor="reply-body">Your reply</label>
                      <textarea
                        id="reply-body"
                        value={replyBody}
                        onChange={(e) => setReplyBody(e.target.value)}
                        placeholder={selectedRequest.awaiting_user ? 'Admin is waiting for your response…' : 'Add a follow-up message…'}
                      />
                    </div>
                    <div className="mock-credits-file-input-wrap">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setReplyFile(e.target.files?.[0] ?? null)}
                      />
                      {replyFile ? ` Selected: ${replyFile.name}` : ' Attach receipt or screenshot (optional)'}
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="mock-credits-modal-footer">
                {selectedRequest.status === 'open' ? (
                  <>
                    <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={handleCancel} disabled={submitting}>
                      Cancel ticket
                    </button>
                    <button type="button" className="mock-credits-btn mock-credits-btn-primary" onClick={handleReply} disabled={submitting}>
                      Send reply
                    </button>
                  </>
                ) : (
                  <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={() => setSelectedRequest(null)}>
                    Close
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </section>
  );
};

export default MockCreditPanel;
